import { useMemo, useState } from 'react';
import { Check, ChevronDown, Facebook, Search } from 'lucide-react';

interface PageOption {
    id: string;
    name: string;
}

interface CompactPageSelectProps {
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    pages?: PageOption[];
}

/** Seletor de página Meta (filtro global da tela) — modelado no CompactProjectSelect. */
export function CompactPageSelect({ value, onChange, pages }: CompactPageSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [busca, setBusca] = useState('');

    // Só páginas tratadas (com nome) — sem nome não aparece no filtro
    const ordenadas = useMemo(
        () =>
            (pages || [])
                .filter((p) => (p.name || '').trim())
                .sort((a, b) =>
                    (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' })
                ),
        [pages]
    );
    const termo = busca.trim().toLowerCase();
    const visiveis = termo
        ? ordenadas.filter(
              (p) => (p.name || '').toLowerCase().includes(termo) || p.id.includes(termo)
          )
        : ordenadas;

    const rotulo = value
        ? ordenadas.find((p) => p.id === value)?.name || value
        : 'Todas as páginas';

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs text-foreground transition-colors hover:bg-accent sm:w-auto"
            >
                <Facebook className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="max-w-[180px] truncate">{rotulo}</span>
                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute left-0 top-full z-50 mt-1 w-72 rounded-md border border-border bg-popover shadow-lg">
                        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                            <Search className="h-3.5 w-3.5 text-muted-foreground" />
                            <input
                                autoFocus
                                value={busca}
                                onChange={(e) => setBusca(e.target.value)}
                                placeholder="Buscar página..."
                                className="w-full bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
                            />
                        </div>
                        <div className="max-h-64 overflow-y-auto p-1">
                            <button
                                type="button"
                                onClick={() => { onChange(undefined); setIsOpen(false); }}
                                className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
                            >
                                Todas as páginas
                                {!value && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                            </button>
                            {visiveis.map((page) => (
                                <button
                                    key={page.id}
                                    type="button"
                                    onClick={() => { onChange(page.id); setIsOpen(false); }}
                                    className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent"
                                >
                                    {/* nome + id: há páginas com nomes idênticos (e sem nome) */}
                                    <span className="min-w-0">
                                        <span className="block truncate">{page.name || '(sem nome)'}</span>
                                        <span className="block text-[10px] text-muted-foreground">{page.id}</span>
                                    </span>
                                    {value === page.id && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" />}
                                </button>
                            ))}
                            {visiveis.length === 0 && (
                                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                                    Nenhuma página encontrada
                                </p>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
